import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material'
import {
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Visibility as PreviewIcon,
  VisibilityOff as PreviewOffIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { updateMessageLike } from '../../../redux/actions/conversationAction'
import { backendClient } from '../../../api/backend'
import Citations from './Citations'
import ToolTimelineDisplay from './ToolTimelineDisplay'
import ExecutionPipelineDisplay from './ExecutionPipelineDisplay'
import ExecutionPlanDisplay from './ExecutionPlanDisplay'
import ExecutionStepsDisplay from './ExecutionStepsDisplay'
import DocumentProcessingStatus from './Chat/DocumentProcessingStatus'
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'

// Use the same worker path as the main PDF viewer
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js'

/**
 * Individual message component with support for streaming and sources
 */
const Message = ({ message, currentLang, isLast, isFirstUserMessage, onPreview, previewContent }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const [showSources, setShowSources] = useState(false)
  const [copied, setCopied] = useState(false)
  const sourcesRef = useRef(null)
  const documentProcessingStatus = useSelector(state => state.conversations.documentProcessingStatus) || []
  const conversation = useSelector(state => state.conversations.conversation)

  const isAssistant = message.role === 'assistant'
  const isStreaming = message.is_streaming
  const hasError = message.error
  const hasSources = message.sources && message.sources.length > 0
  const uniqueSourcesCount = useMemo(() => {
    if (!hasSources) return 0
    const seen = new Set()
    message.sources.forEach((source) => {
      const docId = source.document_id || source.metadata?.document_id
      if (docId) seen.add(docId)
      else seen.add(source)
    })
    return seen.size
  }, [message.sources, hasSources])

  // Determine attached documents to display for user messages
  const attachedDocuments = useMemo(() => {
    if (isAssistant) return []
    // Current session: stored on the message itself
    if (message.attached_documents?.length > 0) return message.attached_documents
    // Reopened conversation: show conversation-level documents on the first user message
    if (isFirstUserMessage && conversation?.conversation_documents?.length > 0) {
      return conversation.conversation_documents.map(doc => ({
        filename: doc.metadata_map?.original_file_name || doc.url?.split('/').pop() || 'Document',
        url: doc.url,
        file_type: doc.metadata_map?.doc_type,
      }))
    }
    return []
  }, [message.attached_documents, isAssistant, isFirstUserMessage, conversation])

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null)
  const [exporting, setExporting] = useState(null) // 'pdf' | 'docx' | 'xlsx' | null
  const exportMenuOpen = Boolean(exportAnchorEl)

  const handleExport = async (format) => {
    setExportAnchorEl(null)
    setExporting(format)
    try {
      // Use artifact content if available, otherwise message content
      const exportContent = firstArtifact?.content || message.content
      const exportTitle = firstArtifact?.title || null
      const blob = await backendClient.exportArtifact(exportContent, format, exportTitle)
      if (!blob) return
      const filename = exportTitle
        ? exportTitle.replace(/[^a-zA-Z0-9À-ÿ _-]/g, '').trim() || 'lawis-export'
        : 'lawis-export'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${filename}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExporting(null)
    }
  }

  // Artifact state
  const hasArtifacts = message.artifacts && message.artifacts.length > 0
  const firstArtifact = hasArtifacts ? message.artifacts[0] : null
  const artifactContent = firstArtifact?.content
  const isPreviewActive = hasArtifacts
    ? previewContent === artifactContent
    : previewContent === message.content

  // Auto-open preview when artifacts arrive (SSE handles this in Chat.js for streaming,
  // this handles reopened conversations loaded from DB)
  const prevArtifactsRef = useRef(message.artifacts)
  useEffect(() => {
    if (hasArtifacts && !prevArtifactsRef.current?.length && isLast && onPreview) {
      onPreview(artifactContent)
    }
    prevArtifactsRef.current = message.artifacts
  }, [message.artifacts, isLast])

  const handleTogglePreview = () => {
    if (!onPreview) return
    if (isPreviewActive) {
      onPreview(null)
    } else {
      onPreview(hasArtifacts ? artifactContent : message.content)
    }
  }

  // Document preview modal state
  const [previewDoc, setPreviewDoc] = useState(null)

  const canPreview = (doc) => ['pdf', 'txt', 'csv'].includes(doc.file_type)
  const hasUrl = (doc) => !!doc.url

  const handleDocClick = (doc) => {
    if (!hasUrl(doc)) return
    if (canPreview(doc)) {
      setPreviewDoc(doc)
    } else {
      // DOCX, PPTX, XLSX — download directly
      window.open(doc.url, '_blank')
    }
  }

  // Get toolExecutions from stored data or transform from activeTools/completedTools for streaming
  const toolExecutions = useMemo(() => {
    const sources = message.sources || []

    // For loaded messages, use stored tool_executions from the database
    if (message.tool_executions && message.tool_executions.length > 0) {
      // Augment retrieval tools with sources if not already present
      return message.tool_executions.map(execution => {
        const isRetrievalTool = ['retrieval', 'search_documents', 'search_by_metadata', 'get_related_documents'].includes(execution.tool_name)
        const hasDocuments = execution.result?.documents?.length > 0 || execution.sources?.length > 0

        // If it's a retrieval tool without documents, attach message sources
        if (isRetrievalTool && !hasDocuments && sources.length > 0) {
          return { ...execution, sources }
        }
        return execution
      })
    }

    // For streaming messages, build from activeTools/completedTools
    const activeTools = message.activeTools || []
    const completedTools = message.completedTools || []
    const executions = []

    // Add completed tools first
    completedTools.forEach((toolName, index) => {
      const execution = {
        event_id: `completed-${toolName}-${index}`,
        tool_name: toolName,
        status: 'completed',
      }
      // Attach sources to search_documents tool for document chips display
      if (toolName === 'search_documents' && sources.length > 0) {
        execution.result = { documents: sources }
      }
      executions.push(execution)
    })

    // Add active tools
    activeTools.forEach((toolName, index) => {
      executions.push({
        event_id: `active-${toolName}-${index}`,
        tool_name: toolName,
        status: 'in_progress',
      })
    })

    return executions
  }, [message.tool_executions, message.activeTools, message.completedTools, message.sources])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const markdownComponents = useMemo(() => ({
    p: ({ children }) => (
      <Typography component="p" sx={{ my: 1, '&:first-of-type': { mt: 0 }, '&:last-of-type': { mb: 0 } }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h6" component="h2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="subtitle1" component="h3" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 3, my: 1, listStyleType: 'disc' }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 3, my: 1, listStyleType: 'decimal' }}>{children}</Box>
    ),
    li: ({ children }) => (
      <Box component="li" sx={{ mb: 0.5 }}>{children}</Box>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>
    ),
    code: ({ inline, className, children }) => {
      if (inline) {
        return (
          <Box
            component="code"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {children}
          </Box>
        )
      }
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: alpha(theme.palette.common.black, 0.05),
            p: 1.5,
            borderRadius: 1,
            overflow: 'auto',
            my: 1,
          }}
        >
          <Box
            component="code"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {children}
          </Box>
        </Box>
      )
    },
    table: ({ children }) => (
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          my: 1,
          '& th, & td': {
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            px: 1.5,
            py: 0.75,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            fontWeight: 600,
          },
        }}
      >
        {children}
      </Box>
    ),
    a: ({ href, children }) => (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: theme.palette.primary.main,
          textDecoration: 'underline',
          '&:hover': { textDecoration: 'none' },
        }}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          pl: 2,
          my: 1,
          color: theme.palette.text.secondary,
        }}
      >
        {children}
      </Box>
    ),
    hr: () => (
      <Box component="hr" sx={{ border: 'none', borderTop: `1px solid ${theme.palette.divider}`, my: 2 }} />
    ),
  }), [theme])

  const isWaitingForContent = isStreaming && !message.content

  const renderContent = () => {
    if (isWaitingForContent) {
      // Show document upload/processing progress instead of empty state
      if (documentProcessingStatus.length > 0) {
        return <DocumentProcessingStatus documents={documentProcessingStatus} />
      }
      return null
    }

    if (isAssistant) {
      return (
        <Box
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.7,
            color: hasError ? theme.palette.error.main : theme.palette.text.primary,
            wordBreak: 'break-word',
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
          {isStreaming && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 16,
                bgcolor: theme.palette.primary.main,
                ml: 0.5,
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 50%': { opacity: 1 },
                  '51%, 100%': { opacity: 0 },
                },
              }}
            />
          )}

          {/* Artifact document cards */}
          {hasArtifacts && !isStreaming && (
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {message.artifacts.map((artifact, idx) => {
                const isActive = previewContent === artifact.content
                const typeIcons = { document: <DocumentIcon />, table: <ExcelIcon />, spreadsheet: <ExcelIcon /> }
                return (
                  <Paper
                    key={idx}
                    onClick={() => {
                      if (onPreview) {
                        onPreview(isActive ? null : artifact.content, artifact.title, artifact.type)
                      }
                    }}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                      borderRadius: 2,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>
                      {typeIcons[artifact.type] || <DocumentIcon />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {artifact.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('click_to_preview')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setExportAnchorEl(e.currentTarget)
                      }}
                      disabled={!!exporting}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                      }}
                    >
                      {exporting ? <CircularProgress size={18} /> : <FileDownloadIcon fontSize="small" />}
                    </IconButton>
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>
      )
    }

    return (
      <Box>
        {attachedDocuments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {attachedDocuments.map((doc, idx) => (
              <Tooltip
                key={idx}
                title={hasUrl(doc)
                  ? (canPreview(doc) ? t('click_to_preview') : t('click_to_download'))
                  : ''
                }
                arrow
                placement="top"
              >
                <Chip
                  icon={<DocumentIcon sx={{ fontSize: 16 }} />}
                  label={doc.filename}
                  size="small"
                  variant="outlined"
                  onClick={hasUrl(doc) ? () => handleDocClick(doc) : undefined}
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                    maxWidth: 200,
                    cursor: hasUrl(doc) ? 'pointer' : 'default',
                    '&:hover': hasUrl(doc) ? {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    } : {},
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        )}
        <Typography
          component="div"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.7,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        flexDirection: currentLang === 'ar' ? 'row-reverse' : 'row',
      }}
    >
      {/* Avatar */}
      {isAssistant ? (
        <Box
          component="img"
          src={theme.palette.mode === 'dark'
            ? '/logo/logo-circle-dark-mode.svg'
            : '/logo/logo-circle.svg'
          }
          alt="Lawis"
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            ...(isWaitingForContent && {
              animation: 'pulse-spin 2s ease-in-out infinite',
              '@keyframes pulse-spin': {
                '0%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
                '50%': { transform: 'scale(1.15) rotate(180deg)', opacity: 0.7 },
                '100%': { transform: 'scale(1) rotate(360deg)', opacity: 1 },
              },
            }),
          }}
        />
      ) : (
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: theme.palette.grey[400],
            flexShrink: 0,
          }}
        >
          <PersonIcon fontSize="small" />
        </Avatar>
      )}

      {/* Message Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Role Label */}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            mb: 0.5,
            display: 'block',
          }}
        >
          {isAssistant ? 'Lawis' : t('you')}
        </Typography>

        {/* Unified Execution Steps Display - for assistant messages */}
        {isAssistant && (
          (message.node_executions && message.node_executions.length > 0) ||
          (message.execution_plan && message.execution_plan.steps && message.execution_plan.steps.length > 0) ||
          (message.tool_executions && message.tool_executions.length > 0) ||
          (message.activeTools && message.activeTools.length > 0) ||
          (message.completedTools && message.completedTools.length > 0)
        ) && (
          <ExecutionStepsDisplay
            message={{
              ...message,
              // Ensure sources are available for tool executions
              sources: message.sources || [],
            }}
            isStreaming={isStreaming}
          />
        )}

        {/* Message Body - hide when waiting for content with no doc processing */}
        {!(isWaitingForContent && documentProcessingStatus.length === 0) && (
          <Paper
            elevation={0}
            {...(isAssistant && !isStreaming ? { 'data-tour': 'chat-answer-content' } : {})}
            sx={{
              p: 2,
              bgcolor: isAssistant
                ? alpha(theme.palette.primary.main, 0.05)
                : alpha(theme.palette.grey[500], 0.08),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            {renderContent()}
          </Paper>
        )}

        {/* Actions Bar - Only for assistant messages */}
        {isAssistant && !isStreaming && message.content && (
          <Box
            data-tour="chat-message-actions"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {/* Copy Button */}
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: copied ? theme.palette.success.main : theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>

            {/* Feedback Buttons */}
            <IconButton
              size="small"
              onClick={() => dispatch(updateMessageLike(message.id, message.like === true ? null : true))}
              sx={{
                color: message.like === true ? theme.palette.success.main : theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.success.main,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                },
              }}
            >
              <ThumbUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => dispatch(updateMessageLike(message.id, message.like === false ? null : false))}
              sx={{
                color: message.like === false ? theme.palette.error.main : theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <ThumbDownIcon fontSize="small" />
            </IconButton>

            {/* Export / Download Button */}
            <Tooltip title={t('export')}>
              <IconButton
                size="small"
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                disabled={!!exporting}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {exporting ? (
                  <CircularProgress size={18} />
                ) : (
                  <FileDownloadIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportMenuOpen}
              onClose={() => setExportAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 160,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  },
                },
              }}
            >
              {firstArtifact?.type !== 'spreadsheet' && (
                <MenuItem onClick={() => handleExport('pdf')}>
                  <ListItemIcon>
                    <PdfIcon fontSize="small" sx={{ color: '#e53935' }} />
                  </ListItemIcon>
                  <ListItemText>{t('export_pdf')}</ListItemText>
                </MenuItem>
              )}
              {firstArtifact?.type !== 'spreadsheet' && (
                <MenuItem onClick={() => handleExport('docx')}>
                  <ListItemIcon>
                    <DocumentIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </ListItemIcon>
                  <ListItemText>{t('export_docx')}</ListItemText>
                </MenuItem>
              )}
              {(!firstArtifact || firstArtifact.type === 'table' || firstArtifact.type === 'spreadsheet') && (
                <MenuItem onClick={() => handleExport('xlsx')}>
                  <ListItemIcon>
                    <ExcelIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                  </ListItemIcon>
                  <ListItemText>{t('export_xlsx')}</ListItemText>
                </MenuItem>
              )}
            </Menu>

            {/* Preview toggle */}
            {onPreview && (
              <Tooltip title={isPreviewActive ? 'Close preview' : 'Preview'}>
                <IconButton
                  size="small"
                  onClick={handleTogglePreview}
                  sx={{
                    color: isPreviewActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {isPreviewActive ? <PreviewOffIcon fontSize="small" /> : <PreviewIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}

            {/* Sources Toggle */}
            {hasSources && (
              <Chip
                data-tour="chat-sources-chip"
                icon={<DocumentIcon fontSize="small" />}
                label={`${uniqueSourcesCount} ${t('sources')}`}
                size="small"
                onClick={() => {
                  const opening = !showSources
                  setShowSources(opening)
                  if (opening) {
                    setTimeout(() => {
                      sourcesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                    }, 350)
                  }
                }}
                deleteIcon={showSources ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onDelete={() => {
                  const opening = !showSources
                  setShowSources(opening)
                  if (opening) {
                    setTimeout(() => {
                      sourcesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                    }, 350)
                  }
                }}
                sx={{
                  ml: 'auto',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            )}
          </Box>
        )}

        {/* Sources Panel */}
        {hasSources && (
          <Collapse in={showSources}>
            <Box ref={sourcesRef} sx={{ mt: 2 }}>
              <Citations
                sources={message.sources}
                currentLang={currentLang}
              />
            </Box>
          </Collapse>
        )}
      </Box>

      {/* Document Preview Modal */}
      {previewDoc && (
        <Dialog
          open={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: '85vh',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DocumentIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                {previewDoc.filename}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={t('download')}>
                <IconButton
                  size="small"
                  onClick={() => window.open(previewDoc.url, '_blank')}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setPreviewDoc(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
            {previewDoc.file_type === 'pdf' ? (
              <PdfPreview url={previewDoc.url} />
            ) : (
              <TextFilePreview url={previewDoc.url} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  )
}

/**
 * Simple text file preview component (for TXT and CSV files)
 */
const TextFilePreview = ({ url }) => {
  const theme = useTheme()
  const [content, setContent] = useState(null)
  const [error, setError] = useState(false)

  React.useEffect(() => {
    if (!url) return
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch')
        return res.text()
      })
      .then(setContent)
      .catch(() => setError(true))
  }, [url])

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography>Failed to load document preview.</Typography>
      </Box>
    )
  }

  if (content === null) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="70%" />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        overflow: 'auto',
        bgcolor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Typography
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          m: 0,
        }}
      >
        {content}
      </Typography>
    </Box>
  )
}

/**
 * PDF preview using react-pdf (already installed in the project)
 */
const PdfPreview = ({ url }) => {
  const theme = useTheme()
  const [numPages, setNumPages] = useState(null)
  const [error, setError] = useState(false)
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(800)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width)
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages)
  }, [])

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography>Failed to load PDF preview.</Typography>
      </Box>
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
        minHeight: '100%',
      }}
    >
      <PdfDocument
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={() => setError(true)}
        loading={
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Box>
        }
      >
        {numPages && Array.from({ length: numPages }, (_, i) => (
          <Box key={i} sx={{ mb: 1, boxShadow: 2 }}>
            <PdfPage
              pageNumber={i + 1}
              width={Math.min(containerWidth - 32, 800)}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Box>
        ))}
      </PdfDocument>
    </Box>
  )
}

export default Message
